import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Balance as BalanceIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  type DocumentData,
} from "firebase/firestore";
import { useAppSelector, type RootState } from "../../../store";
import { admin } from "../../../features/session/sessionSlice";
import firestoreInstance from "../../../services/firestoreInstance";

type BoardRow = {
  boardId: string;
  name: string;
  label: string;
  stored: number;
  actual: number;
};

type OrphanParticipation = {
  tableauId: string;
  count: number;
};

function readNbParticipants(data: DocumentData): number {
  const raw = data.nbParticipants;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return Math.max(0, Math.floor(raw));
  }
  return 0;
}

function readBoardLabel(data: DocumentData): { name: string; label: string } {
  const name = typeof data.name === "string" ? data.name : "";
  const label = typeof data.label === "string" ? data.label : "";
  return { name, label };
}

function participationTableauId(data: DocumentData): string | null {
  const t = data.tableauId;
  if (typeof t === "string" && t.length > 0) {
    return t;
  }
  return null;
}

const BoardNbParticipantsAudit: React.FC = () => {
  const tournoi = useAppSelector((state: RootState) => state.tournoi.tournoi);
  const tournamentId = tournoi?.id;
  const isAdmin = useAppSelector(admin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<BoardRow[]>([]);
  const [orphans, setOrphans] = useState<OrphanParticipation[]>([]);
  const [totalParticipationDocs, setTotalParticipationDocs] = useState<
    number | null
  >(null);
  const [aligningId, setAligningId] = useState<string | null>(null);
  const [aligningAll, setAligningAll] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const runAnalysis = useCallback(async () => {
    if (!tournamentId) {
      setError("Aucun tournoi courant : impossible de charger les tableaux.");
      setRows([]);
      setOrphans([]);
      setTotalParticipationDocs(null);
      return;
    }

    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      const db = firestoreInstance;
      const [tableauxSnap, participationSnap] = await Promise.all([
        getDocs(collection(db, "tournois", tournamentId, "tableaux")),
        getDocs(collection(db, "tournois", tournamentId, "participation")),
      ]);

      setTotalParticipationDocs(participationSnap.size);

      const countByTableau = new Map<string, number>();
      participationSnap.forEach((p) => {
        const tid = participationTableauId(p.data());
        if (!tid) {
          return;
        }
        countByTableau.set(tid, (countByTableau.get(tid) ?? 0) + 1);
      });

      const boardIds = new Set<string>();
      const nextRows: BoardRow[] = [];
      tableauxSnap.forEach((boardDoc) => {
        const id = boardDoc.id;
        boardIds.add(id);
        const data = boardDoc.data();
        const { name, label } = readBoardLabel(data);
        const stored = readNbParticipants(data);
        const actual = countByTableau.get(id) ?? 0;
        nextRows.push({
          boardId: id,
          name,
          label,
          stored,
          actual,
        });
      });

      nextRows.sort((a, b) => {
        const ka = `${a.label} ${a.name}`.trim().toLowerCase();
        const kb = `${b.label} ${b.name}`.trim().toLowerCase();
        return ka.localeCompare(kb, "fr");
      });

      setRows(nextRows);

      const orphanList: OrphanParticipation[] = [];
      countByTableau.forEach((count, tableauId) => {
        if (!boardIds.has(tableauId)) {
          orphanList.push({ tableauId, count });
        }
      });
      orphanList.sort((a, b) => b.count - a.count);
      setOrphans(orphanList);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Erreur lors du chargement Firestore.";
      setError(message);
      setRows([]);
      setOrphans([]);
      setTotalParticipationDocs(null);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  const mismatches = rows.filter((r) => r.stored !== r.actual);

  const handleRealignOne = async (boardId: string, actual: number) => {
    if (!tournamentId || !isAdmin) {
      return;
    }
    setAligningId(boardId);
    setFeedback(null);
    try {
      const ref = doc(
        firestoreInstance,
        "tournois",
        tournamentId,
        "tableaux",
        boardId
      );
      await updateDoc(ref, { nbParticipants: actual });
      setFeedback({
        type: "success",
        text: `Compteur du tableau ${boardId} mis à jour : ${actual} inscription(s) (participations réelles).`,
      });
      await runAnalysis();
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : "Échec de la mise à jour du compteur (voir la console).";
      setFeedback({ type: "error", text: msg });
    } finally {
      setAligningId(null);
    }
  };

  const handleRealignAll = async () => {
    if (!tournamentId || !isAdmin || mismatches.length === 0) {
      return;
    }
    setAligningAll(true);
    setFeedback(null);
    try {
      for (const r of mismatches) {
        const ref = doc(
          firestoreInstance,
          "tournois",
          tournamentId,
          "tableaux",
          r.boardId
        );
        await updateDoc(ref, { nbParticipants: r.actual });
      }
      setFeedback({
        type: "success",
        text: `${mismatches.length} tableau(x) réaligné(s) sur le nombre réel de documents dans « participation ».`,
      });
      await runAnalysis();
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : "Échec partiel ou total de la mise à jour (voir la console).";
      setFeedback({ type: "error", text: msg });
    } finally {
      setAligningAll(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <BalanceIcon color="primary" />
        Cohérence des compteurs d’inscriptions par tableau
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Page non référencée dans le menu — accès direct par URL{" "}
        <code>/admin/board-nb-participants-audit</code>. Pour le tournoi courant,
        chaque document de la sous-collection <code>tableaux</code> expose{" "}
        <code>nbParticipants</code>, qui doit correspondre au nombre de documents
        dans <code>participation</code> avec le même <code>tableauId</code>. La
        réalignation écrit uniquement <code>nbParticipants</code> (aucune
        modification des participations).
      </Alert>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />
          }
          onClick={() => void runAnalysis()}
          disabled={loading || !tournamentId}
        >
          Analyser
        </Button>
        {totalParticipationDocs !== null && tournamentId && (
          <Typography variant="body2" color="text.secondary">
            {rows.length} tableau(x) — {totalParticipationDocs} document(s) dans{" "}
            <code>participation</code> — tournoi : {tournoi?.label ?? tournamentId}
          </Typography>
        )}
        {mismatches.length > 0 && (
          <Tooltip
            title={
              !isAdmin
                ? "Réservé aux comptes avec le rôle admin (claim Firestore)."
                : "Met à jour nbParticipants pour tous les tableaux listés comme incohérents."
            }
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={
                  aligningAll ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
                disabled={
                  !isAdmin || loading || aligningAll || aligningId !== null
                }
                onClick={() => void handleRealignAll()}
              >
                Réaligner tous les écarts ({mismatches.length})
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {!tournamentId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sélectionnez un tournoi courant dans l’application pour lancer
          l’analyse.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {feedback && (
        <Alert
          severity={feedback.type}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.text}
        </Alert>
      )}

      {rows.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip
            color={mismatches.length ? "warning" : "success"}
            label={`Écarts nbParticipants : ${mismatches.length} / ${rows.length}`}
          />
          {orphans.length > 0 && (
            <Chip
              color="error"
              label={`Participations orphelines (tableau inconnu) : ${orphans.length} id(s)`}
            />
          )}
        </Stack>
      )}

      {rows.length > 0 && mismatches.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Tous les compteurs <code>nbParticipants</code> correspondent au nombre
          de participations par <code>tableauId</code>.
        </Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tableau</TableCell>
                <TableCell align="right">
                  <code>nbParticipants</code> (doc)
                </TableCell>
                <TableCell align="right">Participations (réel)</TableCell>
                <TableCell align="right">Écart</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const delta = r.actual - r.stored;
                const ok = delta === 0;
                return (
                  <TableRow
                    key={r.boardId}
                    sx={
                      ok
                        ? undefined
                        : { bgcolor: "action.hover" }
                    }
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.label || r.name || "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", display: "block" }}
                      >
                        {r.boardId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{r.stored}</TableCell>
                    <TableCell align="right">{r.actual}</TableCell>
                    <TableCell align="right">
                      {ok ? (
                        "—"
                      ) : (
                        <Typography
                          component="span"
                          color={delta > 0 ? "warning.main" : "error.main"}
                          fontWeight={600}
                        >
                          {delta > 0 ? "+" : ""}
                          {delta}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {ok ? (
                        "—"
                      ) : (
                        <Tooltip
                          title={
                            !isAdmin
                              ? "Réservé aux comptes admin Firestore."
                              : `Écrire nbParticipants = ${r.actual}`
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                aligningId === r.boardId ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SyncIcon fontSize="small" />
                                )
                              }
                              disabled={
                                !isAdmin ||
                                loading ||
                                aligningAll ||
                                aligningId !== null
                              }
                              onClick={() => void handleRealignOne(r.boardId, r.actual)}
                            >
                              Réaligner
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {orphans.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Participations sans tableau correspondant
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Ces <code>tableauId</code> apparaissent dans{" "}
            <code>participation</code> mais aucun document du même id n’existe
            dans <code>tableaux</code>. Corriger manuellement ou restaurer le
            tableau ; cette page ne supprime pas de participations.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <code>tableauId</code>
                  </TableCell>
                  <TableCell align="right">Documents</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orphans.map((o) => (
                  <TableRow key={o.tableauId}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{o.tableauId}</TableCell>
                    <TableCell align="right">{o.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default BoardNbParticipantsAudit;
